import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { format, parseISO } from "date-fns";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import type {
  CertificateState,
  CompanyCertificate,
  SupplierCompany,
} from "@shared-core/data/supplier-portal-adapter";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { useLoad } from "@shared-core/domain/useLoad";
import {
  ASSESSMENT_OUTCOME_LABEL,
  ASSESSMENT_REASON_LABEL,
} from "@shared-core/domain/supplier-assessment";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { FilePicker } from "../../app/components/patterns/FilePicker";
import { useCompanySummary } from "../shell/CompanySummaryContext";
import {
  EmptyState,
  LoadFailed,
  LoadingCard,
  SectionHeading,
} from "../shell/states";

const day = (iso: string) => format(parseISO(iso), "d MMM yyyy");

/**
 * `/company` (login only): what Pollards holds about the supplier's company. An open assessment is the card at the
 * top; the certificates and past assessments stay (docs/features/supplier-diligence/04, Ben's option B).
 */
export function CompanyPage() {
  const adapter = useSupplierPortalAdapter();
  const summary = useCompanySummary();
  const navigate = useNavigate();
  const [updateError, setUpdateError] = useState<string | null>(null);
  const {
    data: company,
    error,
    isLoading,
    reload,
  } = useLoad(() => adapter.getCompany(), "company");

  if (error && !company)
    return (
      <LoadFailed
        what="your company"
        message={mutationErrorMessage(error, "Check your connection.")}
        onRetry={reload}
      />
    );
  if (isLoading && !company) return <LoadingCard what="your company" />;
  if (!company) return null;

  const isEmpty =
    !company.approval &&
    !company.assessment &&
    company.past_assessments.length === 0 &&
    company.certificates.length === 0;

  return (
    <div className="grid gap-4">
      <h1 className="text-lg font-semibold">Your company</h1>
      {isEmpty ? (
        <Card>
          <EmptyState title="Nothing here yet">
            Pollards will ask you for an assessment of your company here.
          </EmptyState>
        </Card>
      ) : (
        <>
          <AssessmentCard
            company={company}
            updateError={updateError}
            onUpdate={async () => {
              setUpdateError(null);
              try {
                await adapter.startAssessmentUpdate();
              } catch (err) {
                setUpdateError(
                  mutationErrorMessage(err, "We couldn’t start your update."),
                );
                return;
              }
              summary.refresh();
              navigate(paths.supplierAssessment());
            }}
          />
          <Certificates
            certificates={company.certificates}
            hrefOf={adapter.companyFileHref}
            onRenew={async (certificateId, file, expiresOn) => {
              await adapter.uploadCertificateRenewal(
                certificateId,
                file,
                expiresOn,
              );
              reload();
              summary.refresh();
            }}
          />
          <PastAssessments company={company} />
        </>
      )}
    </div>
  );
}

const STAYS_APPROVED =
  "Your company stays approved while Pollards checks the changes.";

/**
 * The assessment card: the one to complete, the one with Pollards, or (with none open) the approval itself and the
 * way to update the answers. A submitted assessment is never reopened; an update is a new one from the current
 * answers (Ben, 3 Nov).
 */
function AssessmentCard({
  company,
  updateError,
  onUpdate,
}: {
  company: SupplierCompany;
  updateError: string | null;
  onUpdate: () => Promise<void>;
}) {
  const { assessment, approval } = company;
  const latest = company.past_assessments[0];

  if (!assessment) {
    if (!approval) return null;
    return (
      <div className="grid gap-2.5 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="font-semibold">Supplier assessment</h2>
          <StatusPill label="Approved" tone="ok" />
        </div>
        <p className="text-sm">
          Pollards approved your company on {day(approval.approved_on)}. Your
          next review is due by {day(approval.next_review_on)}: Pollards will
          send you the form then.
        </p>
        <p className="text-sm text-muted-foreground">
          Has something changed, such as your quality manager, your
          accreditations or how you deliver? Update your answers and Pollards
          will check the changes. Your company stays approved meanwhile.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void onUpdate()}>
            Update your answers
          </Button>
          {latest && (
            <Button asChild variant="outline">
              <Link to={paths.supplierAssessment({ id: latest.id })}>
                See your answers
              </Link>
            </Button>
          )}
        </div>
        {updateError && (
          <p role="alert" className="text-sm text-red-600">
            {updateError}
          </p>
        )}
      </div>
    );
  }

  const isUpdate = assessment.reason === "update";
  if (assessment.submitted_on) {
    return (
      <div className="grid gap-2 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="font-semibold">
            {isUpdate ? "Your update" : "Supplier assessment"}
          </h2>
          <StatusPill label="With Pollards" tone="info" />
        </div>
        <p className="text-sm text-muted-foreground">
          {isUpdate
            ? `You sent your update on ${day(assessment.submitted_on)}. ${STAYS_APPROVED}`
            : `You submitted your ${assessment.form.label.toLowerCase()} on ${day(assessment.submitted_on)}. Pollards’ ISO manager is reviewing it and will email you when your company is approved.`}
        </p>
        <div>
          <Button asChild variant="outline" size="sm">
            <Link to={paths.supplierAssessment({ id: assessment.id })}>
              See your answers
            </Link>
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="grid gap-2.5 rounded-lg border border-primary bg-primary/10 p-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="font-semibold">
          {isUpdate ? "Your update" : "Supplier assessment"}
        </h2>
        <StatusPill label="To complete" tone="todo" />
      </div>
      <p className="text-sm">
        {isUpdate
          ? `Started on ${day(assessment.requested_on)}, from your answers of ${day(assessment.started_from_on ?? assessment.requested_on)}. Change what’s different, then submit it. ${STAYS_APPROVED}`
          : `${assessment.form.label}, about ${assessment.form.minutes} minutes. Requested ${assessment.requested_by ? `by ${assessment.requested_by} ` : ""}on ${day(assessment.requested_on)}. Pollards’ ISO manager reviews it to approve your company as a supplier.`}
      </p>
      <p className="text-sm text-muted-foreground">
        Your answers are saved as you go, so you can stop and come back.
      </p>
      <div>
        <Button asChild>
          <Link to={paths.supplierAssessment()}>
            {isUpdate ? "Continue your update" : "Continue your assessment"}
          </Link>
        </Button>
      </div>
    </div>
  );
}

const CERTIFICATE_STATE: Record<
  CertificateState,
  (c: CompanyCertificate) => {
    label: string;
    tone: "ok" | "warn" | "info";
  }
> = {
  accepted: () => ({ label: "Accepted", tone: "ok" }),
  to_check: () => ({ label: "With Pollards to check", tone: "info" }),
  renew: (c) => ({
    label: c.expires_on ? `Renew by ${day(c.expires_on)}` : "Renew now",
    tone: "warn",
  }),
};

type RenewCertificate = (
  certificateId: string,
  file: File,
  expiresOn: string | null,
) => Promise<void>;

function Certificates({
  certificates,
  hrefOf,
  onRenew,
}: {
  certificates: CompanyCertificate[];
  hrefOf: (fileId: string) => string;
  onRenew: RenewCertificate;
}) {
  return (
    <section aria-labelledby="certificates" className="grid gap-2">
      <SectionHeading id="certificates">Certificates</SectionHeading>
      {certificates.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          The certificates you attach to your assessment appear here, with when
          each one expires. We’ll email you before one runs out.
        </p>
      ) : (
        <Card className="gap-0 overflow-x-auto py-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Certificate</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <span className="sr-only">Renewal</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {certificates.map((c) => {
                const state = CERTIFICATE_STATE[c.state](c);
                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <a
                        href={hrefOf(c.file.file_id)}
                        className="text-xs text-muted-foreground underline underline-offset-2"
                      >
                        {c.file.name}
                      </a>
                    </TableCell>
                    <TableCell>
                      {c.expires_on ? day(c.expires_on) : "—"}
                    </TableCell>
                    <TableCell>
                      <StatusPill label={state.label} tone={state.tone} />
                    </TableCell>
                    <TableCell className="text-right">
                      {c.state === "renew" && (
                        <Renewal certificate={c} onRenew={onRenew} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </section>
  );
}

/** The renewal: when the new certificate expires, then the file (kept as it is chosen). */
function Renewal({
  certificate,
  onRenew,
}: {
  certificate: CompanyCertificate;
  onRenew: RenewCertificate;
}) {
  const [expiresOn, setExpiresOn] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = `renew-${certificate.id}`;
  const upload = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setIsUploading(true);
    try {
      await onRenew(certificate.id, file, expiresOn || null);
    } catch (err) {
      const [first] = extractFieldErrors(err);
      setError(
        first?.message ??
          mutationErrorMessage(err, "We couldn’t keep that file. Try again."),
      );
    } finally {
      setIsUploading(false);
    }
  };
  return (
    <div className="grid justify-items-end gap-1.5">
      <label
        htmlFor={`${id}-expires`}
        className="text-xs text-muted-foreground"
      >
        Expires on
      </label>
      <Input
        id={`${id}-expires`}
        type="date"
        value={expiresOn}
        onChange={(event) => setExpiresOn(event.target.value)}
        className="h-8 w-40 border-gray-300 bg-card shadow-xs dark:border-input"
      />
      <label htmlFor={id} className="sr-only">
        Renewal of {certificate.name}
      </label>
      <FilePicker
        id={id}
        file={null}
        accept="application/pdf,image/*"
        prompt=""
        chooseLabel={isUploading ? "Keeping the file…" : "Upload the renewal"}
        compact
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(file) => void upload(file)}
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function PastAssessments({ company }: { company: SupplierCompany }) {
  if (company.past_assessments.length === 0) return null;
  return (
    <section aria-labelledby="past-assessments" className="grid gap-2">
      <SectionHeading id="past-assessments">Past assessments</SectionHeading>
      <Card className="gap-0 overflow-x-auto py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Assessment</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>
                <span className="sr-only">Answers</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {company.past_assessments.map((p) => (
              <TableRow key={p.id}>
                <TableCell>
                  <div className="font-medium">
                    {ASSESSMENT_REASON_LABEL[p.reason]}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {p.form.label}
                  </div>
                </TableCell>
                <TableCell>{p.submitted_on && day(p.submitted_on)}</TableCell>
                <TableCell>
                  {p.outcome && p.accepted_on && (
                    <StatusPill
                      label={`${ASSESSMENT_OUTCOME_LABEL[p.outcome]} ${day(p.accepted_on)}`}
                      tone="ok"
                    />
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="outline" size="sm">
                    <Link to={paths.supplierAssessment({ id: p.id })}>
                      See the answers
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </section>
  );
}
